import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { TextField, Chip, Checkbox, Tooltip } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaBell, FaSearch, FaCopy, FaCheck,
  FaRobot, FaChartLine, FaChevronLeft, FaChevronRight,
  FaUser, FaRegStickyNote, FaTimes, FaEnvelopeOpenText,
  FaExclamationTriangle, FaCheckCircle, FaCommentDots,
  FaTag, FaPlus, FaHome, FaUpload, FaLink,
} from "react-icons/fa";
import { useViewScope } from "../contexts/ViewScopeContext";
import UploadContactsModal from "../components/UploadContactsModal";
import UploadMboxModal from "../components/UploadMboxModal";
import ContactNotificationModal from "../components/ContactNotificationModal";
import AIConversationModal from "../components/AIConversationModal";
import ContactSearchesModal from "../components/ContactSearchesModal";
import ContactShareLinksModal from "../components/ContactShareLinksModal";
import ContactActivityModal from "../components/ContactActivityModal";
import LeadScoreBadge from "../components/LeadScoreBadge";
import ContactTagPicker, { ContactTagChip } from "../components/ContactTagPicker";
import {
  AIHandoverNotesPopover,
  useHandoverNotesPopover,
  getContactKey,
  getContactIdentity,
} from "../components/AIHandoverNotesPopover";
import DEMO_CONTACTS from "../demo/demoContacts";
import { DEMO_PENDING_REVIEW_KEYS } from "../demo/demoContactDetails";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";
import useAgentHasNoContacts from "../demo/useAgentHasContacts";

// Tags excluded by default on /contacts. Users can remove the chip to opt back
// in. Kept at module scope so the demo-mode check below can distinguish the
// default from a user-applied exclusion.
const DEFAULT_EXCLUDE_TAG_FILTER = ["do-not-contact"];

// Mirrors the wording used by AIConversations so the toast copy stays
// consistent everywhere the AI/Me toggle appears.
function ownershipToastMessage(aiOwns, count = 1) {
  if (count > 1) {
    return aiOwns
      ? `AI now owns ${count.toLocaleString()} relationships — it'll handle replies and outbound follow-ups.`
      : `You own ${count.toLocaleString()} relationships — the AI won't send outbound follow-ups.`;
  }
  return aiOwns
    ? "AI now owns this relationship — it'll handle replies and outbound follow-ups."
    : "You own this relationship — the AI won't send outbound follow-ups.";
}

function ContactsToast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const sev = toast.severity || "success";
  const bg = sev === "error" ? "#dc2626" : sev === "info" ? "#0d9488" : "#16a34a";
  const Icon = sev === "error" ? FaExclamationTriangle : sev === "info" ? FaRobot : FaCheckCircle;
  return (
    // z-index sits below the mobile side-nav (1200) and its overlay (1100)
    // so the toast tucks behind the drawer when it's open, but still floats
    // above ordinary page content.
    <div style={{
      position: "fixed", right: 20, bottom: 20, zIndex: 1000,
      background: bg, color: "#fff",
      padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
      boxShadow: "0 6px 16px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 8,
      maxWidth: 420,
    }}>
      <Icon style={{ flexShrink: 0 }} /> <span>{toast.message}</span>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy"}
      style={{
        background: "none", border: "none", cursor: "pointer", padding: 2,
        display: "inline-flex", alignItems: "center",
        color: copied ? "#22c55e" : "#9ca3af", fontSize: 12, flexShrink: 0,
      }}
    >
      {copied ? <FaCheck /> : <FaCopy />}
    </button>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function sourceLabel(source) {
  if (!source) return "—";
  const map = {
    agent_box_contact: "AgentBox",
    eagle_contact: "Eagle",
    id4me_contact: "ID4me",
    lockedon_contact: "LockedOn",
    propertyme_contact: "PropertyMe",
    mailbox_contact: "Mailbox",
    domain_illawarra_contact: "Domain Illawarra",
    uploaded_csv: "CSV Upload",
    static_csv: "CSV",
  };
  return map[source] || source.replace(/_/g, " ");
}

// Hoisted + memoized so toggling parent-level state (e.g. opening the
// notifications modal, switching the AI conversation key, paginating) does
// NOT re-mount every row. Previously this was defined inside `Contacts`,
// which gave it a brand-new component identity on every render — React
// then unmounted/remounted all 50 rows (each with MUI Tooltip, Checkbox,
// Chip) on every click, which was the actual source of the perceived lag
// on /contacts.
const ContactCard = React.memo(function ContactCard({
  contact,
  isMobile,
  selected,
  needsReview,
  notesPinned,
  onToggleSelect,
  onSetOwnership,
  onOpenAiConvo,
  onOpenActivity,
  onOpenNotif,
  onOpenSearches,
  onOpenShareLinks,
  onNotesEnter: openNotesHover,
  onNotesLeave: closeNotesHover,
  onNotesPin: pinNotesPopover,
  onAddTag,
  onRemoveTag,
}) {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unknown";
  const hasNotifTarget = contact.email || contact.clean_mobile || contact.mobile;
  const n = contact.user_settings?.notifications;
  const hasPropertyUpdates = n?.property_updates?.enabled;
  const hasMarketUpdates = n?.market_updates?.enabled;
  const hasAnyNotif = hasPropertyUpdates || hasMarketUpdates;

  const key = getContactKey(contact);
  const serverAiOwns = contact.ai_owns_relationship !== false; // default true
  // Local visual state so the toggle flips instantly on click without
  // waiting for the parent re-render of the entire contacts list.
  // Synced back whenever the underlying contact prop changes (incl. rollback).
  const [pendingAiOwns, setPendingAiOwns] = useState(null);
  useEffect(() => { setPendingAiOwns(null); }, [serverAiOwns]);
  const aiOwns = pendingAiOwns !== null ? pendingAiOwns : serverAiOwns;
  const notesCount = contact.handover_notes_count || 0;

  const notesBtnRef = useRef(null);
  const tagBtnRef = useRef(null);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const tags = Array.isArray(contact.tags) ? contact.tags : [];
  const appliedSlugs = useMemo(() => new Set(tags.map((t) => t.slug)), [tags]);

  const handleNotesEnter = (e) => {
    if (!key) return;
    openNotesHover(notesBtnRef.current || e.currentTarget, contact);
  };
  const handleNotesLeave = () => {
    if (notesPinned) return;
    closeNotesHover();
  };
  const handleNotesClick = (e) => {
    e.stopPropagation();
    if (!key) return;
    pinNotesPopover(notesBtnRef.current || e.currentTarget, contact);
  };

  // Action icons are always visible (previously hover-revealed).
  const actionsVisible = true;

  // Shared style helper for the icon-only action buttons. Keeps a
  // consistent 32px square footprint regardless of which icon is inside.
  const iconBtnStyle = (active = false) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 32, height: 32, borderRadius: 8,
    background: active ? "#f0fdfa" : "#fff",
    border: `1px solid ${active ? "#99f6e4" : "#e5e7eb"}`,
    color: active ? "#0f766e" : "#6b7280",
    cursor: "pointer", padding: 0,
    position: "relative",
    transition: "background 0.12s, border-color 0.12s, color 0.12s",
  });

  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      border: `1px solid ${selected ? "#0d9488" : "#e5e7eb"}`,
      padding: isMobile ? "14px 16px" : "16px 20px",
      display: "flex", flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 10 : 0,
      alignItems: isMobile ? "stretch" : "center",
      transition: "box-shadow 0.15s, border-color 0.15s",
    }}
      onMouseEnter={e => {
        if (isMobile) return;
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
      }}
      onMouseLeave={e => {
        if (isMobile) return;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Selection checkbox */}
      <Checkbox
        size="small"
        checked={selected}
        disabled={!key}
        onChange={() => onToggleSelect(key)}
        sx={{
          p: 0.5,
          mr: isMobile ? 0 : 1,
          color: "#9ca3af",
          "&.Mui-checked": { color: "#0d9488" },
          alignSelf: isMobile ? "flex-start" : "center",
        }}
      />

      {/* Left: Name + contact info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
          <LeadScoreBadge
            score={contact.lead_score}
            components={contact.lead_components}
            lastEventAt={contact.lead_last_event_at}
          />
          <Chip
            label={sourceLabel(contact.source)}
            size="small"
            sx={{ fontSize: 10, height: 20, maxWidth: 100, flexShrink: 0 }}
            variant="outlined"
          />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? "4px 12px" : "4px 20px", fontSize: 13, color: "#6b7280" }}>
          {contact.email && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
              <FaEnvelope style={{ fontSize: 11, flexShrink: 0, color: "#9ca3af" }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.email}</span>
              <CopyButton text={contact.email} />
            </span>
          )}
          {(contact.clean_mobile || contact.mobile) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FaPhone style={{ fontSize: 11, flexShrink: 0, color: "#9ca3af" }} />
              <span>{contact.clean_mobile || contact.mobile}</span>
              <CopyButton text={contact.clean_mobile || contact.mobile} />
            </span>
          )}
          {(contact.suburb || contact.residence_address) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
              <FaMapMarkerAlt style={{ fontSize: 11, flexShrink: 0, color: "#9ca3af" }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contact.residence_address || [contact.suburb, contact.state].filter(Boolean).join(", ")}
              </span>
            </span>
          )}
        </div>
        {/* Tag strip + "+ Tag" picker trigger. Always renders the trigger so
            the agent can add the first tag inline without opening a modal. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6, alignItems: "center" }}>
          {tags.map((t) => (
            <ContactTagChip
              key={t.slug}
              tag={t}
              onRemove={key ? () => onRemoveTag?.(contact, t) : undefined}
            />
          ))}
          <button
            ref={tagBtnRef}
            onClick={(e) => { e.stopPropagation(); if (!key) return; setTagPickerOpen((v) => !v); }}
            disabled={!key}
            title="Add a tag"
            style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              padding: "2px 6px", borderRadius: 999,
              background: "transparent",
              border: "1px dashed #d1d5db",
              color: "#6b7280", fontSize: 10, fontWeight: 600,
              cursor: key ? "pointer" : "not-allowed",
            }}
          >
            <FaPlus style={{ fontSize: 8 }} /> Tag
          </button>
        </div>
        {tagPickerOpen && (
          <ContactTagPicker
            anchorEl={tagBtnRef.current}
            appliedSlugs={appliedSlugs}
            onClose={() => setTagPickerOpen(false)}
            onPick={(tagDef) => { onAddTag?.([contact], tagDef); }}
            onRemove={(tagDef) => { onRemoveTag?.(contact, tagDef); }}
          />
        )}
      </div>

      {/* Right: hover-revealed action icons + AI/Me toggle (always pinned far right) */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
        ...(isMobile
          ? { borderTop: "1px solid #f3f4f6", paddingTop: 10, flexWrap: "wrap" }
          : { marginLeft: 16 }),
      }}>
        {/* Action icon row — only revealed on hover (desktop) or always
            visible on mobile / when a popover is pinned. We keep the row
            mounted (just hidden) so the buttons preserve their refs and
            the layout doesn't reflow on hover. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          visibility: actionsVisible ? "visible" : "hidden",
          opacity: actionsVisible ? 1 : 0,
          transition: "opacity 0.12s ease",
          pointerEvents: actionsVisible ? "auto" : "none",
        }}>
          {/* AI Conversation */}
          <Tooltip arrow placement="top" title={needsReview ? "AI Conversation — draft needs your review" : "Open AI Conversation"}>
            <span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!key) return;
                  onOpenAiConvo(key);
                }}
                disabled={!key}
                style={{
                  ...iconBtnStyle(needsReview),
                  background: needsReview ? "#fef3c7" : "#fff",
                  borderColor: needsReview ? "#fcd34d" : "#e5e7eb",
                  color: needsReview ? "#92400e" : "#6b7280",
                }}
              >
                <FaCommentDots style={{ fontSize: 14 }} />
                {needsReview && (
                  <span
                    style={{
                      position: "absolute", top: -4, right: -4,
                      width: 14, height: 14, borderRadius: "50%",
                      background: "#f59e0b", color: "#fff",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8, fontWeight: 700,
                      border: "1.5px solid #fff",
                    }}
                  >
                    !
                  </span>
                )}
              </button>
            </span>
          </Tooltip>

          {/* Activity */}
          {contact.email && (
            <Tooltip arrow placement="top" title="View activity timeline">
              <button
                onClick={(e) => { e.stopPropagation(); onOpenActivity?.(contact); }}
                style={iconBtnStyle()}
              >
                <FaChartLine style={{ fontSize: 14 }} />
              </button>
            </Tooltip>
          )}

          {/* Property Searches */}
          {(contact.email || contact.clean_mobile || contact.mobile) && (
            <Tooltip arrow placement="top" title="Past property searches & estimates">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const q = contact.email || contact.clean_mobile || contact.mobile;
                  onOpenSearches?.(q, contact);
                }}
                style={iconBtnStyle()}
              >
                <FaHome style={{ fontSize: 14 }} />
              </button>
            </Tooltip>
          )}

          {/* Share Links — mint a personalised ?t= link to paste into an SMS */}
          {(contact.email || contact.clean_mobile || contact.mobile) && (
            <Tooltip arrow placement="top" title="Copy a personalised SMS link">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenShareLinks?.(contact); }}
                style={iconBtnStyle()}
              >
                <FaLink style={{ fontSize: 13 }} />
              </button>
            </Tooltip>
          )}

          {/* AI Handover Notes — hover opens popover, click pins */}
          <Tooltip arrow placement="top" title={notesCount > 0 ? `${notesCount} AI note${notesCount === 1 ? "" : "s"}` : "AI Notes"}>
            <span>
              <button
                ref={notesBtnRef}
                data-notes-key={key || ""}
                onMouseEnter={handleNotesEnter}
                onMouseLeave={handleNotesLeave}
                onClick={handleNotesClick}
                disabled={!key}
                style={{
                  ...iconBtnStyle(notesCount > 0),
                  background: notesCount > 0 ? "#fffbeb" : "#fff",
                  borderColor: notesCount > 0 ? "#fde68a" : "#e5e7eb",
                  color: notesCount > 0 ? "#92400e" : "#6b7280",
                }}
              >
                <FaRegStickyNote style={{ fontSize: 14 }} />
                {notesCount > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    minWidth: 14, height: 14, padding: "0 3px", borderRadius: 999,
                    background: "#f59e0b", color: "#fff",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, fontWeight: 700,
                    border: "1.5px solid #fff",
                  }}>
                    {notesCount}
                  </span>
                )}
              </button>
            </span>
          </Tooltip>

          {/* Notification settings */}
          {hasNotifTarget && (
            <Tooltip
              arrow
              placement="top"
              title={
                hasAnyNotif
                  ? `Notifications on${hasPropertyUpdates ? " · Property" : ""}${hasMarketUpdates ? " · Market" : ""}`
                  : "Set up notifications"
              }
            >
              <button
                onClick={(e) => { e.stopPropagation(); onOpenNotif(contact); }}
                style={{
                  ...iconBtnStyle(hasAnyNotif),
                  background: hasAnyNotif ? "#f0fdf4" : "#fff",
                  borderColor: hasAnyNotif ? "#bbf7d0" : "#e5e7eb",
                  color: hasAnyNotif ? "#15803d" : "#6b7280",
                }}
              >
                <FaBell style={{ fontSize: 14 }} />
              </button>
            </Tooltip>
          )}
        </div>

        {/* AI / Me ownership pill toggle — always visible, pinned to the
            far right of the card. */}
        <Tooltip
          arrow
          placement="top"
          enterDelay={300}
          title={
            aiOwns
              ? "AI owns this relationship — it will reply to messages and follow up automatically. Click Me to take over."
              : "You own this relationship — the AI will stay quiet and leave replies to you. Click AI to hand it back."
          }
        >
          <div
            role="radiogroup"
            aria-label="Who owns this relationship"
            style={{
              display: "inline-flex", borderRadius: 999,
              border: "1px solid #e5e7eb", background: "#f9fafb",
              padding: 2, gap: 2,
              marginLeft: isMobile ? 0 : "auto",
            }}
          >
            <button
              role="radio"
              aria-checked={aiOwns}
              disabled={!key}
              onClick={(e) => {
                e.stopPropagation();
                if (aiOwns) return;
                setPendingAiOwns(true); // instant visual flip
                // Defer the parent setContacts + network call so this
                // card's pill repaints in the next frame instead of
                // being batched with the (heavy) full-list re-render.
                setTimeout(() => onSetOwnership([contact], true), 0);
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 999, border: "none",
                background: aiOwns ? "#0d9488" : "transparent",
                color: aiOwns ? "#fff" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: key ? "pointer" : "not-allowed",
                // Only transition the background — see Me button below.
                transition: "background 0.12s ease",
              }}
            >
              <FaRobot style={{ fontSize: 11 }} /> AI
            </button>
            <button
              role="radio"
              aria-checked={!aiOwns}
              disabled={!key}
              onClick={(e) => {
                e.stopPropagation();
                if (!aiOwns) return;
                setPendingAiOwns(false); // instant visual flip
                // Defer the parent setContacts + network call so this
                // card's pill repaints in the next frame instead of
                // being batched with the (heavy) full-list re-render.
                setTimeout(() => onSetOwnership([contact], false), 0);
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 999, border: "none",
                background: !aiOwns ? "#1f2937" : "transparent",
                color: !aiOwns ? "#fff" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: key ? "pointer" : "not-allowed",
                // Only transition the background — fading the colour at the
                // same time briefly leaves white text over a half-transparent
                // dark pill (i.e. light parent showing through), which reads
                // as a "white on white" flash.
                transition: "background 0.12s ease",
              }}
            >
              <FaUser style={{ fontSize: 10 }} /> Me
            </button>
          </div>
        </Tooltip>
      </div>
    </div>
  );
});

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMboxModal, setShowMboxModal] = useState(false);
  const [notifContact, setNotifContact] = useState(null);
  // Contact key whose AI conversation thread is currently shown in the
  // modal overlay. null means the modal is closed.
  const [aiConvoKey, setAiConvoKey] = useState(null);
  const [activityContact, setActivityContact] = useState(null);
  // When set, opens the per-contact Property Searches modal. Holds
  // { query, label } where query is the email/mobile passed to the
  // embedded PropertySearches list and label is shown in the modal header.
  const [searchesModal, setSearchesModal] = useState(null);
  const [shareLinksContact, setShareLinksContact] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;
  const { viewScope, agentFilter } = useViewScope();

  // --- Bulk select + AI ownership state ---
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // When true, the bulk action targets every contact matching the current
  // filters across all pages (not just the visible page).
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [ownershipToast, setOwnershipToast] = useState(null);

  // Set of contact_keys that have at least one AI draft awaiting review.
  // Loaded once on mount from /ai-outbound-queue/list (the same source the
  // AI Conversations "Needs Review" tab uses) so each contact row can show
  // a "Needs review" badge without changing the /get-contacts payload.
  const [pendingReviewKeys, setPendingReviewKeys] = useState(() => new Set());

  // Tag filter: list of slugs the user is filtering by. Reset to page 1 on
  // change. Empty array = no tag filter applied. The filter chip row at the
  // top of the header lets the user add/remove slugs interactively.
  const [tagFilter, setTagFilter] = useState([]);
  const [tagFilterPickerAnchor, setTagFilterPickerAnchor] = useState(null);
  const tagFilterBtnRef = useRef(null);
  // Exclude-tag filter: list of slugs to exclude. A contact is hidden if it
  // has ANY of these tags applied.
  const [excludeTagFilter, setExcludeTagFilter] = useState(DEFAULT_EXCLUDE_TAG_FILTER);
  const [excludeTagFilterPickerAnchor, setExcludeTagFilterPickerAnchor] = useState(null);
  const excludeTagFilterBtnRef = useRef(null);
  // "Needs review" filter — when true, only return contacts with at least
  // one pending draft in production.ai_agent_outbound_queue (same definition
  // as the AI Conversations → Needs Review tab).
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const bulkTagBtnRef = useRef(null);
  const [bulkTagPickerOpen, setBulkTagPickerOpen] = useState(false);

  // AI Handover Notes popover (shared across Contacts + AI Conversations).
  // The hook owns the open/pin/hover state and timers; we just wire button
  // events to it and render <AIHandoverNotesPopover {...popoverProps} /> once.
  const handleNotesCountChangedRef = useRef(null);
  const {
    popoverProps: notesPopoverProps,
    openHover: openNotesHover,
    openPinned: pinNotesPopover,
    closeHover: closeNotesHover,
    pinned: notesPinned,
  } = useHandoverNotesPopover({
    isMobile,
    onNotesChanged: (contact, delta) => handleNotesCountChangedRef.current?.(contact, delta),
  });

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const searchDebounceRef = useRef(null);

  const fetchContacts = useCallback(async (searchTerm, pg) => {
    setLoading(true);
    try {
      const body = { view_scope: viewScope, agent_filter: agentFilter, page: pg || 1, page_size: pageSize };
      if (searchTerm) body.search = searchTerm;
      if (tagFilter.length > 0) body.tag_filter = tagFilter;
      if (excludeTagFilter.length > 0) body.exclude_tag_filter = excludeTagFilter;
      if (needsReviewOnly) body.needs_review_only = true;
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/get-contacts`,
        body
      );
      const data = res.data?.contacts || [];
      setContacts(data.map((c, i) => ({ ...c, id: i })));
      setTotalCount(res.data?.total_count ?? data.length);
    } catch (e) {
      console.error("Failed to fetch contacts:", e);
      setContacts([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [viewScope, agentFilter, tagFilter, excludeTagFilter, needsReviewOnly]);

  useEffect(() => {
    fetchContacts(search, page);
  }, [fetchContacts, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the AI "needs review" queue once so we can flag contact rows that
  // currently have a draft awaiting the agent's approval. We derive the key
  // the same way getContactKey does (lower(email) || trimmed mobile) so the
  // membership lookup matches what each row computes.
  const refreshPendingReview = useCallback(() => {
    return handleTokenAndPostRequest(
      `${window.api_host}/ai-outbound-queue/list`,
      { status: "pending" },
      { "Content-Type": "application/json" }
    ).then((res) => {
      if (res.data && res.data.status === "success") {
        const items = Array.isArray(res.data.items) ? res.data.items : [];
        const keys = new Set();
        // Backend contact_key is the raw lower(email) or trimmed mobile
        // (no "e:"/"m:" prefix). Re-normalise to match getContactKey()'s
        // prefixed form so Set membership lookups in ContactCard match.
        for (const it of items) {
          const raw = (it.contact_key || "").trim();
          const email = raw.includes("@")
            ? raw.toLowerCase()
            : (it.contact_email || "").trim().toLowerCase();
          if (email) { keys.add(`e:${email}`); continue; }
          const mobile = (raw && !raw.includes("@") ? raw : (it.contact_phone || "")).trim();
          if (mobile) keys.add(`m:${mobile}`);
        }
        setPendingReviewKeys(keys);
      }
    }).catch(() => { /* non-fatal — badge just won't show */ });
  }, []);
  useEffect(() => {
    refreshPendingReview();
  }, [refreshPendingReview]);

  // Debounced search — resets to page 1
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchContacts(search, 1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 whenever the tag filter changes; the fetch happens via
  // the [fetchContacts, page] effect (fetchContacts depends on tagFilter).
  useEffect(() => {
    setPage(1);
  }, [tagFilter, excludeTagFilter, needsReviewOnly]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalCount);

  // When the agent has no contacts at all (and isn't narrowing the list with a
  // search/filter), show a populated sample view instead of a bare empty state
  // so they can see what Contacts looks like once their leads come in. Demo
  // interactions are sandboxed by useDemoNetworkGuard — local state updates but
  // nothing reaches the backend.
  //
  // Only show the sample data when the backend confirms the agent has no
  // rows in production.contacts_merged. Filter state is intentionally NOT
  // considered — an empty filtered result for an agent who does have data
  // must show a real empty state, not the demo.
  const agentHasNoContacts = useAgentHasNoContacts();
  const showDemo = agentHasNoContacts === true && !loading;
  // Relax the guard while the upload modal is open so a real upload can reach
  // the backend (the CTA's whole purpose is to let the user leave the demo).
  useDemoNetworkGuard(showDemo && !showUploadModal);
  const displayContacts = showDemo ? DEMO_CONTACTS : contacts;
  const displayCount = showDemo ? DEMO_CONTACTS.length : totalCount;
  // In the demo, /ai-outbound-queue/list is blocked so pendingReviewKeys is
  // empty — seed the "needs review" badge from the sample contacts that carry
  // a pending AI draft so the AI Conversation button lights up to match.
  const displayPendingReviewKeys = showDemo
    ? new Set(DEMO_PENDING_REVIEW_KEYS)
    : pendingReviewKeys;

  // --- Selection helpers ---
  const pageKeys = useMemo(
    () => contacts.map(getContactKey).filter(Boolean),
    [contacts]
  );
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.has(k));
  const someOnPageSelected = !allOnPageSelected && pageKeys.some((k) => selectedKeys.has(k));
  // Treat selectAllMatching as "every row is selected" for visual purposes.
  const masterChecked = selectAllMatching || allOnPageSelected;
  const masterIndeterminate = !selectAllMatching && someOnPageSelected;

  const toggleSelect = useCallback((key) => {
    if (!key) return;
    // Any per-row interaction exits the "select all matching" mode.
    setSelectAllMatching(false);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectAllMatching || allOnPageSelected) {
      // Clear everything.
      setSelectAllMatching(false);
      setSelectedKeys(new Set());
    } else {
      // Select every matching contact (across all pages).
      setSelectAllMatching(true);
      setSelectedKeys(new Set(pageKeys));
    }
  }, [selectAllMatching, allOnPageSelected, pageKeys]);

  // Keep visible-page rows visually checked while in select-all mode, even
  // when paginating to a new page.
  useEffect(() => {
    if (!selectAllMatching) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      pageKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [selectAllMatching, pageKeys]);

  const clearSelection = useCallback(() => {
    setSelectAllMatching(false);
    setSelectedKeys(new Set());
  }, []);

  // Optimistic per-row + bulk mutation of ai_owns_relationship
  const applyOwnershipLocal = useCallback((keys, value) => {
    setContacts((prev) => prev.map((c) => {
      const k = getContactKey(c);
      if (k && keys.has(k)) return { ...c, ai_owns_relationship: value };
      return c;
    }));
  }, []);

  const setOwnership = useCallback(async (contactsToUpdate, aiOwns) => {
    const keys = new Set(contactsToUpdate.map(getContactKey).filter(Boolean));
    if (keys.size === 0) return;
    const payload = {
      contacts: contactsToUpdate.map(getContactIdentity),
      ai_owns_relationship: aiOwns,
    };
    // Snapshot for rollback — derive from the passed-in rows so this
    // callback doesn't need to close over `contacts` (which would break
    // its stable identity and force every memoized ContactCard to
    // re-render on every state change).
    const snapshot = contactsToUpdate
      .map((c) => ({ key: getContactKey(c), value: c.ai_owns_relationship }))
      .filter((s) => s.key);
    applyOwnershipLocal(keys, aiOwns);
    // Show the confirmation toast immediately — the API call runs in the
    // background and only surfaces a toast if it actually fails (rollback).
    setOwnershipToast({ severity: "info", message: ownershipToastMessage(aiOwns, keys.size) });
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/agent-set-contact-ai-ownership`,
        payload,
      );
    } catch (e) {
      console.error("Failed to update AI ownership:", e);
      // rollback
      setContacts((prev) => prev.map((c) => {
        const k = getContactKey(c);
        const snap = snapshot.find((s) => s.key === k);
        return snap ? { ...c, ai_owns_relationship: snap.value } : c;
      }));
      setOwnershipToast({ severity: "error", message: e?.response?.data?.message || "Failed to update ownership" });
    }
  }, [applyOwnershipLocal]);

  const bulkSetOwnership = useCallback(async (aiOwns) => {
    setBulkSaving(true);
    try {
      if (selectAllMatching) {
        // Server-side bulk update across every matching contact.
        // Optimistically flip every visible row.
        setContacts((prev) => prev.map((c) => ({ ...c, ai_owns_relationship: aiOwns })));
        const snapshot = contacts.map((c) => ({ key: getContactKey(c), value: c.ai_owns_relationship }));
        // Show the confirmation toast immediately; let the API call run in
        // the background and only surface a toast if it fails.
        setOwnershipToast({ severity: "info", message: ownershipToastMessage(aiOwns, totalCount || 2) });
        try {
          await handleTokenAndPostRequest(
            `${window.api_host}/agent-set-contact-ai-ownership`,
            {
              ai_owns_relationship: aiOwns,
              select_all_matching: true,
              view_scope: viewScope,
              agent_filter: agentFilter,
              search,
            },
          );
        } catch (e) {
          console.error("Failed to bulk update AI ownership:", e);
          // Roll back visible page only.
          setContacts((prev) => prev.map((c) => {
            const k = getContactKey(c);
            const snap = snapshot.find((s) => s.key === k);
            return snap ? { ...c, ai_owns_relationship: snap.value } : c;
          }));
          setOwnershipToast({ severity: "error", message: e?.response?.data?.message || "Failed to update ownership" });
        }
        return;
      }
      const selected = contacts.filter((c) => selectedKeys.has(getContactKey(c)));
      if (selected.length === 0) return;
      await setOwnership(selected, aiOwns);
    } finally {
      setBulkSaving(false);
    }
  }, [contacts, selectedKeys, selectAllMatching, setOwnership, viewScope, agentFilter, search, totalCount]);

  const handleNotesCountChanged = useCallback((contact, delta) => {
    const targetKey = getContactKey(contact);
    if (!targetKey) return;
    setContacts((prev) => prev.map((c) => {
      if (getContactKey(c) !== targetKey) return c;
      const cur = c.handover_notes_count || 0;
      return { ...c, handover_notes_count: Math.max(0, cur + delta) };
    }));
  }, []);
  // Keep the ref in sync so the hook (set up earlier in render) can call
  // the latest handler without re-creating the popover.
  useEffect(() => {
    handleNotesCountChangedRef.current = handleNotesCountChanged;
  }, [handleNotesCountChanged]);

  // --- Tag mutations (per-row + bulk). Both are optimistic so the chip
  // shows up instantly; if the API call fails we roll back and toast. ---
  const applyTagLocal = useCallback((keys, tagDef, mode /* 'add' | 'remove' */) => {
    setContacts((prev) => prev.map((c) => {
      const k = getContactKey(c);
      if (!k || !keys.has(k)) return c;
      const cur = Array.isArray(c.tags) ? c.tags : [];
      if (mode === "remove") {
        return { ...c, tags: cur.filter((t) => t.slug !== tagDef.slug) };
      }
      if (cur.some((t) => t.slug === tagDef.slug)) return c;
      return {
        ...c,
        tags: [
          ...cur,
          {
            slug: tagDef.slug, label: tagDef.label, color: tagDef.color,
            category: tagDef.category, source: "human",
          },
        ],
      };
    }));
  }, []);

  // Replace the tag list on a single contact row in-place. Used by the
  // AI Conversation modal to mirror tag edits made inside the modal back
  // into the /contacts list (and vice versa via the externalTags prop).
  const replaceContactTags = useCallback((contactKey, newTags) => {
    if (!contactKey) return;
    setContacts((prev) => prev.map((c) => {
      const k = getContactKey(c);
      if (k !== contactKey) return c;
      return { ...c, tags: Array.isArray(newTags) ? newTags : [] };
    }));
  }, []);

  const addTagToContacts = useCallback(async (contactsToUpdate, tagDef) => {
    if (!tagDef?.slug) return;
    const keys = new Set(contactsToUpdate.map(getContactKey).filter(Boolean));
    if (keys.size === 0) return;
    applyTagLocal(keys, tagDef, "add");
    setOwnershipToast({
      severity: "info",
      message: keys.size > 1
        ? `Tagged ${keys.size.toLocaleString()} contacts with "${tagDef.label}"`
        : `Tagged "${tagDef.label}"`,
    });
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/add`,
        {
          tag_slugs: [tagDef.slug],
          contacts: contactsToUpdate.map(getContactIdentity),
        },
      );
    } catch (e) {
      console.error("Failed to add tag:", e);
      applyTagLocal(keys, tagDef, "remove");
      setOwnershipToast({ severity: "error", message: e?.response?.data?.message || "Failed to add tag" });
    }
  }, [applyTagLocal]);

  const removeTagFromContact = useCallback(async (contact, tagDef) => {
    if (!tagDef?.slug) return;
    const key = getContactKey(contact);
    if (!key) return;
    applyTagLocal(new Set([key]), tagDef, "remove");
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/remove`,
        {
          tag_slug: tagDef.slug,
          contacts: [getContactIdentity(contact)],
        },
      );
    } catch (e) {
      console.error("Failed to remove tag:", e);
      // Roll back by re-adding the tag with whatever metadata we have.
      applyTagLocal(new Set([key]), tagDef, "add");
      setOwnershipToast({ severity: "error", message: e?.response?.data?.message || "Failed to remove tag" });
    }
  }, [applyTagLocal]);

  const bulkAddTag = useCallback(async (tagDef) => {
    if (!tagDef?.slug) return;
    const selected = contacts.filter((c) => selectedKeys.has(getContactKey(c)));
    if (selected.length === 0) return;
    await addTagToContacts(selected, tagDef);
  }, [contacts, selectedKeys, addTagToContacts]);


  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
      background: "#f6f8fa", boxSizing: "border-box", width: "100%", overflowX: "hidden", overflowY: "auto",
    }}>
      <div style={{ maxWidth: 1200, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>

        <div style={{
          position: isMobile ? "static" : "sticky", top: 0, zIndex: 10, background: "#f6f8fa",
          paddingTop: isMobile ? 72 : 32, paddingBottom: 8,
          marginLeft: isMobile ? -16 : -32, marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32, paddingRight: isMobile ? 16 : 32,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, margin: 0, color: "#1f2937" }}>Contacts</h1>
            {!loading && (
              <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 400 }}>
                {displayCount.toLocaleString()} total
              </span>
            )}
          </div>
          {!isMobile && !loading && !showDemo && totalCount > 0 && (
            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 12, marginTop: 2 }}>
              Showing {showingFrom}–{showingTo} of {totalCount.toLocaleString()}
            </p>
          )}

          {/* Search + select-all */}
          <div style={{ marginBottom: isMobile ? 10 : 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px", maxWidth: 400 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by name, email, mobile, suburb..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <FaSearch style={{ color: "#9ca3af", marginRight: 8, fontSize: 14 }} />,
                }}
              />
            </div>
            {!loading && contacts.length > 0 && (
              <label
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 13, color: "#6b7280", cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={masterChecked}
                  indeterminate={masterIndeterminate}
                  onChange={toggleSelectAll}
                  sx={{ p: 0.5, color: "#9ca3af", "&.Mui-checked": { color: "#0d9488" }, "&.MuiCheckbox-indeterminate": { color: "#0d9488" } }}
                />
                Select all
              </label>
            )}
            <span style={{
              fontSize: 12, color: "#6b7280", lineHeight: 1.4,
              maxWidth: 280, whiteSpace: "normal",
            }}>
              Import your inbox (Gmail, Outlook or Apple Mail) so AI can
              enrich each contact with a summary, relationship notes and
              key dates.
            </span>
            <button
              onClick={() => setShowMboxModal(true)}
              title="Import your inbox from Gmail, Outlook or Apple Mail so AI can summarise email history onto each contact."
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "7px 12px", borderRadius: 8,
                background: "#fff", color: "#0f766e",
                border: "1px solid #0f766e", fontSize: 13, fontWeight: 600,
                cursor: "pointer", whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdfa"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
            >
              <FaEnvelopeOpenText style={{ fontSize: 12 }} /> Import Email History
            </button>
          </div>

          {/* Tag filter chip row. Always renders the "Filter by tag" trigger so
              the agent can layer tag filters on top of search/agent scope. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: isMobile ? 10 : 14 }}>
            <button
              ref={tagFilterBtnRef}
              onClick={() => setTagFilterPickerAnchor((cur) => (cur ? null : tagFilterBtnRef.current))}
              title="Filter contacts by tag"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 999,
                background: tagFilter.length > 0 ? "#ecfeff" : "#fff",
                border: `1px solid ${tagFilter.length > 0 ? "#67e8f9" : "#e5e7eb"}`,
                color: tagFilter.length > 0 ? "#0e7490" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              <FaTag style={{ fontSize: 10 }} />
              {tagFilter.length === 0 ? "Filter by tag" : `${tagFilter.length} tag filter${tagFilter.length === 1 ? "" : "s"}`}
            </button>
            {tagFilter.map((slug) => (
              <span
                key={slug}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 8px", borderRadius: 999,
                  background: "#0e7490", color: "#fff",
                  fontSize: 11, fontWeight: 600,
                }}
              >
                {slug}
                <button
                  onClick={() => setTagFilter((cur) => cur.filter((s) => s !== slug))}
                  title="Remove filter"
                  style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 0, display: "inline-flex" }}
                >
                  <FaTimes style={{ fontSize: 9 }} />
                </button>
              </span>
            ))}
            {tagFilter.length > 0 && (
              <button
                onClick={() => setTagFilter([])}
                style={{
                  background: "transparent", border: "none",
                  color: "#6b7280", fontSize: 12, cursor: "pointer", textDecoration: "underline",
                }}
              >
                Clear
              </button>
            )}
            {/* Exclude-tag filter chip row. Mirrors the include filter above
                but hides contacts that have ANY of the selected tags. */}
            <button
              ref={excludeTagFilterBtnRef}
              onClick={() => setExcludeTagFilterPickerAnchor((cur) => (cur ? null : excludeTagFilterBtnRef.current))}
              title="Hide contacts that have any of these tags"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 999,
                background: excludeTagFilter.length > 0 ? "#fef2f2" : "#fff",
                border: `1px solid ${excludeTagFilter.length > 0 ? "#fca5a5" : "#e5e7eb"}`,
                color: excludeTagFilter.length > 0 ? "#991b1b" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              <FaTag style={{ fontSize: 10 }} />
              {excludeTagFilter.length === 0 ? "Exclude tag" : `${excludeTagFilter.length} excluded`}
            </button>
            {excludeTagFilter.map((slug) => (
              <span
                key={`x-${slug}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 8px", borderRadius: 999,
                  background: "#991b1b", color: "#fff",
                  fontSize: 11, fontWeight: 600,
                }}
              >
                {slug}
                <button
                  onClick={() => setExcludeTagFilter((cur) => cur.filter((s) => s !== slug))}
                  title="Remove exclude filter"
                  style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 0, display: "inline-flex" }}
                >
                  <FaTimes style={{ fontSize: 9 }} />
                </button>
              </span>
            ))}
            {excludeTagFilter.length > 0 && (
              <button
                onClick={() => setExcludeTagFilter([])}
                style={{
                  background: "transparent", border: "none",
                  color: "#6b7280", fontSize: 12, cursor: "pointer", textDecoration: "underline",
                }}
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setNeedsReviewOnly((cur) => !cur)}
              title="Show only contacts with a pending AI draft awaiting review"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 999,
                background: needsReviewOnly ? "#fef3c7" : "#fff",
                border: `1px solid ${needsReviewOnly ? "#fcd34d" : "#e5e7eb"}`,
                color: needsReviewOnly ? "#92400e" : "#6b7280",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              <FaExclamationTriangle style={{ fontSize: 10 }} />
              Needs review
              {needsReviewOnly && (
                <FaTimes
                  style={{ fontSize: 9, marginLeft: 2 }}
                  onClick={(e) => { e.stopPropagation(); setNeedsReviewOnly(false); }}
                />
              )}
            </button>
            {tagFilterPickerAnchor && (
              <ContactTagPicker
                anchorEl={tagFilterPickerAnchor}
                appliedSlugs={new Set(tagFilter)}
                onClose={() => setTagFilterPickerAnchor(null)}
                onPick={(tagDef) => {
                  setTagFilter((cur) => (cur.includes(tagDef.slug) ? cur : [...cur, tagDef.slug]));
                }}
                onRemove={(tagDef) => {
                  setTagFilter((cur) => cur.filter((s) => s !== tagDef.slug));
                }}
              />
            )}
            {excludeTagFilterPickerAnchor && (
              <ContactTagPicker
                anchorEl={excludeTagFilterPickerAnchor}
                appliedSlugs={new Set(excludeTagFilter)}
                onClose={() => setExcludeTagFilterPickerAnchor(null)}
                onPick={(tagDef) => {
                  setExcludeTagFilter((cur) => (cur.includes(tagDef.slug) ? cur : [...cur, tagDef.slug]));
                }}
                onRemove={(tagDef) => {
                  setExcludeTagFilter((cur) => cur.filter((s) => s !== tagDef.slug));
                }}
              />
            )}
          </div>

          {/* Bulk action bar */}
          {(selectedKeys.size > 0 || selectAllMatching) && (
            <div style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
              padding: "10px 14px", borderRadius: 10,
              background: "#ecfeff",
              border: "1px solid #a5f3fc",
              marginBottom: isMobile ? 10 : 16,
            }}>
              <span style={{ fontSize: 13, color: "#0e7490", fontWeight: 600 }}>
                {selectAllMatching
                  ? `All ${totalCount.toLocaleString()} matching selected`
                  : `${selectedKeys.size} selected`}
              </span>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: "#6b7280" }}>Set ownership:</span>
              <button
                disabled={bulkSaving}
                onClick={() => bulkSetOwnership(true)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 999,
                  background: "#0d9488", color: "#fff",
                  border: "none", fontSize: 12, fontWeight: 600,
                  cursor: bulkSaving ? "wait" : "pointer", opacity: bulkSaving ? 0.7 : 1,
                }}
              >
                <FaRobot style={{ fontSize: 11 }} /> AI owns
              </button>
              <button
                disabled={bulkSaving}
                onClick={() => bulkSetOwnership(false)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 999,
                  background: "#1f2937", color: "#fff",
                  border: "none", fontSize: 12, fontWeight: 600,
                  cursor: bulkSaving ? "wait" : "pointer", opacity: bulkSaving ? 0.7 : 1,
                }}
              >
                <FaUser style={{ fontSize: 10 }} /> I own
              </button>
              <button
                onClick={clearSelection}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 10px", borderRadius: 8,
                  background: "transparent", color: "#0e7490",
                  border: "1px solid #a5f3fc", fontSize: 12, fontWeight: 500, cursor: "pointer",
                }}
              >
                <FaTimes style={{ fontSize: 10 }} /> Clear
              </button>
              {/* Bulk-add tag — only available when an explicit selection is
                  active (i.e. not the cross-page "all matching" mode). */}
              {!selectAllMatching && (
                <button
                  ref={bulkTagBtnRef}
                  disabled={bulkSaving}
                  onClick={() => setBulkTagPickerOpen((v) => !v)}
                  title="Tag selected contacts"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "6px 12px", borderRadius: 999,
                    background: "#fff", color: "#0e7490",
                    border: "1px solid #67e8f9", fontSize: 12, fontWeight: 600,
                    cursor: bulkSaving ? "wait" : "pointer", opacity: bulkSaving ? 0.7 : 1,
                  }}
                >
                  <FaTag style={{ fontSize: 10 }} /> Add tag…
                </button>
              )}
              {bulkTagPickerOpen && (
                <ContactTagPicker
                  anchorEl={bulkTagBtnRef.current}
                  appliedSlugs={new Set()}
                  onClose={() => setBulkTagPickerOpen(false)}
                  onPick={(tagDef) => {
                    setBulkTagPickerOpen(false);
                    bulkAddTag(tagDef);
                  }}
                />
              )}
            </div>
          )}
        </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 12, padding: isMobile ? "14px 16px" : "16px 20px",
              border: "1px solid #e5e7eb",
              display: "flex", flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 10 : 16,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Skeleton width={120 + Math.random() * 60} height={16} />
                  <Skeleton width={60} height={20} borderRadius={10} />
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <Skeleton width={160 + Math.random() * 40} height={13} />
                  <Skeleton width={100 + Math.random() * 20} height={13} />
                </div>
              </div>
              <Skeleton width={140} height={32} borderRadius={8} />
            </div>
          ))}
        </div>
      ) : contacts.length === 0 && !showDemo ? (
        <div style={{
          textAlign: "center", padding: "60px 20px", color: "#9ca3af",
          background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
        }}>
          <FaSearch style={{ fontSize: 28, marginBottom: 12, color: "#d1d5db" }} />
          <div style={{ fontSize: 16, fontWeight: 500 }}>No contacts match your filters</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Try clearing your search, tag filters, or the "Needs review" toggle</div>
        </div>
      ) : (
        <>
          {showDemo && (
            <DemoBanner
              isMobile={isMobile}
              title="Here's how Contacts looks with leads"
              subtitle="Sample data — upload your contacts and Vendorleads will match, score and nurture them for you."
              ctaLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><FaUpload size={13} /> Upload Contacts</span>}
              onCta={() => setShowUploadModal(true)}
            />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {displayContacts.map((c) => {
              const k = getContactKey(c);
              return (
                <ContactCard
                  key={c.id}
                  contact={c}
                  isMobile={isMobile}
                  selected={k ? selectedKeys.has(k) : false}
                  needsReview={k ? displayPendingReviewKeys.has(k) : false}
                  notesPinned={notesPinned}
                  onToggleSelect={toggleSelect}
                  onSetOwnership={setOwnership}
                  onOpenAiConvo={setAiConvoKey}
                  onOpenActivity={setActivityContact}
                  onOpenNotif={setNotifContact}
                  onOpenSearches={(q, c2) => setSearchesModal({
                    query: q,
                    label: [c2?.first_name, c2?.last_name].filter(Boolean).join(" ")
                      || c2?.email || c2?.clean_mobile || c2?.mobile || "",
                  })}
                  onOpenShareLinks={setShareLinksContact}
                  onNotesEnter={openNotesHover}
                  onNotesLeave={closeNotesHover}
                  onNotesPin={pinNotesPopover}
                  onAddTag={addTagToContacts}
                  onRemoveTag={removeTagFromContact}
                />
              );
            })}
          </div>

          {/* Pagination */}
          {!showDemo && totalPages > 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
              padding: "16px 0", marginTop: 8,
            }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb",
                  background: page <= 1 ? "#f9fafb" : "#fff", color: page <= 1 ? "#d1d5db" : "#374151",
                  fontSize: 13, fontWeight: 500, cursor: page <= 1 ? "default" : "pointer",
                }}
              >
                <FaChevronLeft style={{ fontSize: 10 }} /> Previous
              </button>
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb",
                  background: page >= totalPages ? "#f9fafb" : "#fff", color: page >= totalPages ? "#d1d5db" : "#374151",
                  fontSize: 13, fontWeight: 500, cursor: page >= totalPages ? "default" : "pointer",
                }}
              >
                Next <FaChevronRight style={{ fontSize: 10 }} />
              </button>
            </div>
          )}
          {/* Spacer so the last row clears the fixed demo banner. */}
          {showDemo && <div style={{ height: isMobile ? 180 : 100 }} />}
        </>
      )}
      <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
      <UploadMboxModal show={showMboxModal} onClose={() => setShowMboxModal(false)} />
      <ContactNotificationModal
        show={!!notifContact}
        onClose={() => setNotifContact(null)}
        contact={notifContact}
        onSaved={(payload) => {
          if (!payload || !notifContact) return;
          const savedKey = getContactKey(notifContact);
          if (!savedKey) return;
          setContacts((prev) => prev.map((c) => {
            if (getContactKey(c) !== savedKey) return c;
            return {
              ...c,
              user_settings: {
                ...(c.user_settings || {}),
                notifications: payload.user_settings,
              },
            };
          }));
        }}
      />
      <AIConversationModal
        contactKey={aiConvoKey}
        onClose={() => {
          setAiConvoKey(null);
          // Refresh the "Needs review" badges — the user may have just
          // generated a follow-up draft (adding a pending queue row) or
          // sent/dismissed an existing one (removing the row), and the
          // /contacts row indicator needs to reflect that without a full
          // page reload.
          refreshPendingReview();
        }}
        onOwnershipChange={(ck, aiOwns) => {
          if (!ck) return;
          applyOwnershipLocal(new Set([ck]), aiOwns);
        }}
        externalTags={
          aiConvoKey
            ? (contacts.find((c) => getContactKey(c) === aiConvoKey)?.tags || [])
            : undefined
        }
        onTagsChange={replaceContactTags}
      />
      <AIHandoverNotesPopover {...notesPopoverProps} />
      {shareLinksContact && (
        <ContactShareLinksModal
          contact={shareLinksContact}
          onClose={() => setShareLinksContact(null)}
        />
      )}
      <ContactSearchesModal
        searchQuery={searchesModal?.query || null}
        contactLabel={searchesModal?.label || ""}
        onClose={() => setSearchesModal(null)}
      />
      <ContactActivityModal
        contact={activityContact}
        onClose={() => setActivityContact(null)}
      />
      <ContactsToast toast={ownershipToast} onClose={() => setOwnershipToast(null)} />
      </div>
    </div>
  );
}
